/**
 * Posta listeleri (backlog #585) — E-posta menüsünün üçüncü bölümü: kullanıcıları Kullanıcılar listesinin süzgeciyle süzüp bir posta listesi kurmak ve o listeye bir posta yazıp göndermek.
 *
 * Her personel açar (izin gerekmez; şirket girişleri taraf kuralıyla geri çevrilir). Listeler personel arasında ortaktır: hepsini okur ve hepsine posta yazar; adını, üyelerini
 * değiştirmek ve silmek yalnızca listeyi oluşturana ya da Sistem Ayarları yetkisi olana aittir (satırdaki `can_edit`; backend 403 verir). Satırdaki üç iş:
 *   · Üyeler  → ListeDetay (üyeleri gör, çıkar, ekle, adı değiştir)
 *   · Posta yaz → ListeMailiYaz (konu + gövde, isteğe bağlı İngilizce, önizleme + onay, iş olarak gönderim)
 *   · Sil     → onay sorusu, sonra liste ve üyelikleri gider (kullanıcılara dokunulmaz)
 */
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, CardBody, Container, Input, Modal, ModalBody, ModalFooter, ModalHeader, Pagination, PaginationItem, PaginationLink, Spinner, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../../../Components/Common/BreadCrumb";
import i18n, { currentLocaleTag } from "../../../../../i18n";
import ListeOlustur from "./ListeOlustur";
import ListeDetay from "./ListeDetay";
import ListeMailiYaz from "./ListeMailiYaz";
import { deleteMailList, listMailLists } from "../../../../../services/postaListeleriService";

export const SAYFA_BOYU = 10;
const ARAMA_BEKLEME_MS = 400;

function tarih(deger) {
    if (!deger) return "";
    const d = new Date(deger);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(currentLocaleTag(), { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const MailListeleri = () => {
    const { t } = useTranslation("moduller");
    useEffect(() => { document.title = i18n.t("moduller:emailsListe.baslikFds"); }, []);

    const [veri, setVeri] = useState({ data: [], total: 0, totalPages: 1 });
    const [yukleniyor, setYukleniyor] = useState(true);
    const [hata, setHata] = useState(null);
    const [sayfa, setSayfa] = useState(1);
    const [aramaGirdisi, setAramaGirdisi] = useState("");
    const [arama, setArama] = useState("");
    const [yenile, setYenile] = useState(0);
    const [olusturAcik, setOlusturAcik] = useState(false);
    const [detay, setDetay] = useState(null);
    const [yaz, setYaz] = useState(null);
    const [silinecek, setSilinecek] = useState(null);
    const [siliyor, setSiliyor] = useState(false);
    const [silmeHatasi, setSilmeHatasi] = useState(null);

    useEffect(() => {
        const zamanlayici = setTimeout(() => {
            const v = aramaGirdisi.trim();
            if (v !== arama) { setArama(v); setSayfa(1); }
        }, ARAMA_BEKLEME_MS);
        return () => clearTimeout(zamanlayici);
    }, [aramaGirdisi, arama]);

    useEffect(() => {
        let ignore = false;
        async function run() {
            setYukleniyor(true);
            try {
                const cevap = await listMailLists({ page: sayfa, limit: SAYFA_BOYU, search: arama });
                if (ignore) return;
                setVeri(cevap);
                setHata(null);
            } catch (e) {
                if (!ignore) { setHata(e.message); setVeri({ data: [], total: 0, totalPages: 1 }); }
            } finally {
                if (!ignore) setYukleniyor(false);
            }
        }
        run();
        return () => { ignore = true; };
    }, [sayfa, arama, yenile]);

    const tazele = () => setYenile((n) => n + 1);

    async function sil() {
        setSiliyor(true);
        setSilmeHatasi(null);
        try {
            await deleteMailList(silinecek.mail_list_id);
            setSilinecek(null);
            tazele();
        } catch (e) {
            setSilmeHatasi(e.message);
        } finally {
            setSiliyor(false);
        }
    }

    const toplamSayfa = Math.max(1, veri.totalPages || 1);

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title={t("emailsListe.baslik")} pageTitle={t("emailsListe.menuBasligi")} />
                <Card>
                    <CardBody>
                        <p className="text-muted">{t("emailsListe.aciklama")}</p>
                        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                            <Input
                                id="listeler-ara" type="search" className="w-auto flex-grow-1" style={{ maxWidth: 360 }} value={aramaGirdisi}
                                placeholder={t("emailsListe.listelerdeAra")} onChange={(e) => setAramaGirdisi(e.target.value)}
                            />
                            <Button color="primary" className="ms-auto" onClick={() => setOlusturAcik(true)}>{t("emailsListe.yeniListe")}</Button>
                        </div>

                        {hata && <Alert color="danger">{hata}</Alert>}
                        {yukleniyor ? <div className="text-center py-4"><Spinner size="sm" /></div> : (
                            <Table responsive className="align-middle">
                                <thead>
                                    <tr>
                                        <th>{t("emailsListe.liste")}</th>
                                        <th>{t("emailsListe.uye")}</th>
                                        <th>{t("emailsListe.olusturanKolon")}</th>
                                        <th>{t("emailsListe.guncelleme")}</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {veri.data.length === 0 && <tr><td colSpan={5} className="text-muted">{t(arama ? "emailsListe.listeBulunamadi" : "emailsListe.listeYok")}</td></tr>}
                                    {veri.data.map((l) => (
                                        <tr key={l.mail_list_id}>
                                            <td>
                                                <div className="fw-medium">{l.mail_list_name}</div>
                                                {l.mail_list_note && <div className="small text-muted">{l.mail_list_note}</div>}
                                            </td>
                                            <td>{l.member_count}</td>
                                            <td>{l.creator_name || "—"}</td>
                                            <td>{tarih(l.updated_at)}</td>
                                            <td className="text-end text-nowrap">
                                                <Button size="sm" color="light" className="me-1" onClick={() => setDetay(l)}>{t("emailsListe.uyeler")}</Button>
                                                <Button size="sm" color="primary" className="me-1" onClick={() => setYaz(l)}>{t("emailsListe.postaYaz")}</Button>
                                                {l.can_edit && <Button size="sm" color="danger" outline onClick={() => { setSilmeHatasi(null); setSilinecek(l); }}>{t("emailsListe.sil")}</Button>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                        {toplamSayfa > 1 && (
                            <Pagination className="justify-content-center mb-0">
                                <PaginationItem disabled={sayfa <= 1}><PaginationLink previous onClick={() => setSayfa(sayfa - 1)} /></PaginationItem>
                                <PaginationItem active><PaginationLink>{sayfa} / {toplamSayfa}</PaginationLink></PaginationItem>
                                <PaginationItem disabled={sayfa >= toplamSayfa}><PaginationLink next onClick={() => setSayfa(sayfa + 1)} /></PaginationItem>
                            </Pagination>
                        )}
                    </CardBody>
                </Card>

                <ListeOlustur isOpen={olusturAcik} toggle={() => setOlusturAcik(false)} onDone={() => { setOlusturAcik(false); setSayfa(1); tazele(); }} />
                <ListeDetay isOpen={Boolean(detay)} liste={detay} toggle={() => setDetay(null)} onChanged={tazele} />
                <ListeMailiYaz isOpen={Boolean(yaz)} liste={yaz} toggle={() => setYaz(null)} />

                <Modal isOpen={Boolean(silinecek)} toggle={() => setSilinecek(null)}>
                    <ModalHeader toggle={() => setSilinecek(null)}>{t("emailsListe.silBaslik")}</ModalHeader>
                    <ModalBody>
                        <p className="mb-0">{t("emailsListe.silSorusu", { ad: silinecek?.mail_list_name })}</p>
                        {silmeHatasi && <Alert color="danger" className="mt-3 mb-0">{silmeHatasi}</Alert>}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="danger" disabled={siliyor} onClick={sil}>{siliyor ? <Spinner size="sm" /> : t("emailsListe.sil")}</Button>
                        <Button color="light" onClick={() => setSilinecek(null)}>{t("common:iptal")}</Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default MailListeleri;
